import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '@shared/state';
import {
  loadTonePreference, setToneOverride, resetTonePreference,
} from '@shared/services/tonePreference';
import {
  effectivePlayfulness, registerFromPlayfulness,
  type TonePreference, type ToneRegister,
} from '@shared/utils/tone-preference';
import { Card } from '../ui/Card';

const REGISTERS: { r: ToneRegister; label: string; blurb: string }[] = [
  { r: 'minimal', label: 'Minimal', blurb: 'Warm and to the point' },
  { r: 'dry', label: 'Dry wit', blurb: 'Clever, with a wink' },
  { r: 'playful', label: 'Playful', blurb: 'More fun and games' },
];

/**
 * "Your Cogni vibe" — transparency + control for the humor-matching loop.
 * We learn quietly from reactions to checkpoints; this is where the user can see
 * what we've learned and override it. (No covert profiling — fully disclosed.)
 */
export function CogniVibeCard() {
  const userId = useAuthStore((s) => s.user?.uid);
  const [pref, setPref] = useState<TonePreference | null>(null);
  const [saving, setSaving] = useState<ToneRegister | 'reset' | null>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    loadTonePreference(userId).then((p) => { if (active) setPref(p); }).catch(() => {});
    return () => { active = false; };
  }, [userId]);

  const currentRegister = pref ? registerFromPlayfulness(effectivePlayfulness(pref)) : 'dry';
  const stillLearning = !pref || pref.confidence < 0.5;

  async function choose(r: ToneRegister) {
    if (!userId) return;
    setSaving(r);
    try { setPref(await setToneOverride(userId, r)); } finally { setSaving(null); }
  }

  async function reset() {
    if (!userId) return;
    setSaving('reset');
    try { setPref(await resetTonePreference(userId)); } finally { setSaving(null); }
  }

  return (
    <Card className="mt-8" padding="lg">
      <div className="flex items-start gap-3 mb-3">
        <Sparkles size={18} className="text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-base font-semibold mb-1">Cogni's vibe</h2>
          <p className="text-xs text-content-muted font-body">
            Professor Cogni tailors its humour to you by learning from how you react to checkpoints —
            nothing hidden, just your reactions. {stillLearning
              ? 'Still getting a read on you; meanwhile it keeps things dry and warm.'
              : `Right now it's leaning ${currentRegister}.`} Nudge it anytime.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {REGISTERS.map(({ r, label, blurb }) => {
          const active = currentRegister === r && !stillLearning;
          return (
            <button
              key={r}
              type="button"
              onClick={() => choose(r)}
              disabled={saving !== null}
              title={blurb}
              className={`px-3 py-2 rounded-lg text-sm font-body border transition-colors cursor-pointer disabled:opacity-50
                ${active
                  ? 'bg-indigo-500/15 border-indigo-500 text-content'
                  : 'bg-dark-subtle border-dark-border text-content-secondary hover:border-indigo-500/50'}`}
            >
              <span className="font-semibold">{label}</span>
              <span className="block text-[11px] text-content-dim">{blurb}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={reset}
        disabled={saving !== null}
        className="mt-3 text-[11px] text-content-dim hover:text-content-muted font-body underline cursor-pointer disabled:opacity-50"
      >
        Reset and let Cogni relearn from scratch
      </button>
    </Card>
  );
}
